import {message} from '@sail/intl';

export const invalidEmailMsg = message({
  id: '@sail/ui.validation.invalidEmail',
  description: 'Error message when an email address is invalid',
  defaultMessage: 'Invalid email address',
});

export const disposableEmailMsg = message({
  id: '@sail/ui.validation.disposableEmail',
  description: 'Error message when a disposable email address is not allowed',
  defaultMessage: 'Disposable email addresses are not allowed',
});

export const emailDomainNotAllowedMsg = message<{domain: string}>({
  id: '@sail/ui.validation.emailDomainNotAllowed',
  description: 'Error message when an email domain is not in the allowed list',
  defaultMessage: 'Email domain {domain} is not allowed',
});

export const emailDomainBlockedMsg = message<{domain: string}>({
  id: '@sail/ui.validation.emailDomainBlocked',
  description: 'Error message when an email domain is blocked',
  defaultMessage: 'Email domain {domain} is not allowed',
});

export const emailTLDNotAllowedMsg = message<{tlds: string}>({
  id: '@sail/ui.validation.emailTLDNotAllowed',
  description: 'Error message when an email TLD is not in the required list',
  defaultMessage: 'Email must use one of these domain extensions: {tlds}',
});

export const emailTooLongMsg = message<{maxLength: number}>({
  id: '@sail/ui.validation.emailTooLong',
  description: 'Error message when an email address is too long',
  defaultMessage: 'Email address must be at most {maxLength} characters',
});

export const invalidUrlMsg = message({
  id: '@sail/ui.validation.invalidUrl',
  description: 'Error message when a URL is invalid',
  defaultMessage: 'Invalid URL',
});

export const httpsRequiredMsg = message({
  id: '@sail/ui.validation.httpsRequired',
  description: 'Error message when HTTPS protocol is required',
  defaultMessage: 'URL must use HTTPS protocol',
});

export const urlProtocolNotAllowedMsg = message<{protocol: string}>({
  id: '@sail/ui.validation.urlProtocolNotAllowed',
  description: 'Error message when URL protocol is not in the allowed list',
  defaultMessage: 'URL protocol {protocol} is not allowed',
});

export const localhostNotAllowedMsg = message({
  id: '@sail/ui.validation.localhostNotAllowed',
  description: 'Error message when localhost URLs are not allowed',
  defaultMessage: 'Localhost URLs are not allowed',
});

export const ipAddressNotAllowedMsg = message({
  id: '@sail/ui.validation.ipAddressNotAllowed',
  description: 'Error message when IP address URLs are not allowed',
  defaultMessage: 'IP address URLs are not allowed',
});

export const urlDomainNotAllowedMsg = message<{domain: string}>({
  id: '@sail/ui.validation.urlDomainNotAllowed',
  description: 'Error message when a URL domain is not in the allowed list',
  defaultMessage: 'URL domain {domain} is not allowed',
});

export const urlDomainBlockedMsg = message<{domain: string}>({
  id: '@sail/ui.validation.urlDomainBlocked',
  description: 'Error message when a URL domain is blocked',
  defaultMessage: 'URL domain {domain} is not allowed',
});

export const urlTLDNotAllowedMsg = message<{tlds: string}>({
  id: '@sail/ui.validation.urlTLDNotAllowed',
  description: 'Error message when a URL TLD is not in the required list',
  defaultMessage: 'URL must use one of these domain extensions: {tlds}',
});

export const urlPathRequiredMsg = message({
  id: '@sail/ui.validation.urlPathRequired',
  description: 'Error message when URL path is required',
  defaultMessage: 'URL must include a path',
});

export const urlQueryParamsNotAllowedMsg = message({
  id: '@sail/ui.validation.urlQueryParamsNotAllowed',
  description: 'Error message when URL query parameters are not allowed',
  defaultMessage: 'URL query parameters are not allowed',
});

export const urlFragmentsNotAllowedMsg = message({
  id: '@sail/ui.validation.urlFragmentsNotAllowed',
  description: 'Error message when URL fragments are not allowed',
  defaultMessage: 'URL fragments are not allowed',
});

export const urlAuthRequiredMsg = message({
  id: '@sail/ui.validation.urlAuthRequired',
  description: 'Error message when URL authentication is required',
  defaultMessage: 'URL must include authentication',
});

export const urlPortNotAllowedMsg = message<{port: number}>({
  id: '@sail/ui.validation.urlPortNotAllowed',
  description: 'Error message when URL port is not in the allowed list',
  defaultMessage: 'URL port {port} is not allowed',
});

export const addressRequiredMsg = message({
  id: '@sail/ui.validation.addressRequired',
  description: 'Error message when address line is required',
  defaultMessage: 'Address is required',
});

// Uses ICU select syntax to show country-specific terminology:
// US (type='city'): "City is required"
// GB (type='post_town'): "Post town is required"
// Hong Kong (type='district'): "District is required"
export const localityRequiredMsg = message<{type: string}>({
  id: '@sail/ui.validation.localityRequired',
  description: 'Error message when locality/city is required',
  defaultMessage:
    '{type, select, city {City} district {District} post_town {Post town} other {City}} is required',
});

// Uses ICU select syntax to show country-specific terminology:
// US (type='state'): "State is required"
// Japan (type='prefecture'): "Prefecture is required"
// Ireland (type='county'): "County is required"
export const subregionRequiredMsg = message<{type: string}>({
  id: '@sail/ui.validation.subregionRequired',
  description: 'Error message when subregion/state is required',
  defaultMessage:
    '{type, select, state {State} province {Province} county {County} prefecture {Prefecture} department {Department} oblast {Oblast} area {Area} district {District} island {Island} parish {Parish} other {State}} is required',
});

// Uses ICU select syntax to show country-specific terminology:
// US (type='zip'): "Postal code is required"
// GB (type='postal'): "Postal code is required"
// Ireland (type='eircode'): "Eircode is required"
export const postalCodeRequiredMsg = message<{type: string}>({
  id: '@sail/ui.validation.postalCodeRequired',
  description: 'Error message when postal code is required',
  defaultMessage:
    '{type, select, zip {Postal code} postal {Postal code} eircode {Eircode} other {Postal code}} is required',
});

export const postalCodeInvalidMsg = message<{type: string}>({
  id: '@sail/ui.validation.postalCodeInvalid',
  description: 'Error message when postal code format is invalid',
  defaultMessage:
    '{type, select, zip {Postal code} postal {Postal code} eircode {Eircode} other {Postal code}} is invalid',
});

export const invalidPhoneNumberMsg = message({
  id: '@sail/ui.validation.invalidPhoneNumber',
  description: 'Error message when a phone number is invalid',
  defaultMessage: 'Invalid phone number',
});

export const phoneNumberCountryNotAllowedMsg = message<{country: string}>({
  id: '@sail/ui.validation.phoneNumberCountryNotAllowed',
  description:
    'Error message when phone number country is not in the allowed list',
  defaultMessage: 'Phone numbers from {country} are not allowed',
});
